import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { PixiSettings } from "../../../../pixiSettings";
import { getAssetsPath } from "../../../../utils/pathConfig";

// ============================================================================
// LOADER SPINE CONFIGURATION
// Fine-tune spine scale and vertical offset per device/orientation
// ============================================================================
const DEVICE_CONFIG_MOBILE_PORTRAIT = {
  scale: 0.325,
  yOffset: 0,
};

const DEVICE_CONFIG_MOBILE_LANDSCAPE = {
  scale: 0.375,
  yOffset: 0,
};

const DEVICE_CONFIG_TABLET_PORTRAIT = {
  scale: 0.5,
  yOffset: 0,
};

const DEVICE_CONFIG_TABLET_LANDSCAPE = {
  scale: 0.5,
  yOffset: 0,
};

const DEVICE_CONFIG_DESKTOP = {
  scale: 0.65,
  yOffset: 0,
};

/**
 * LoaderController — displays the loading_screen spine animation.
 * Flow: play "in" → queue "loop" (looping) → when done loading, play "out" → resolve.
 */
class LoaderController {
  constructor(container, dimensions, dimensionsContext) {
    this.container = container;
    this.container.sortableChildren = true;
    this.dimensions = dimensions;
    this.dimensionsContext = dimensionsContext;
    this.spine = null;
    this.gradientSprite = null;
    this.gradientTexture = null;
    this.isInitialized = false;
    this.inComplete = false;
    this.loadingComplete = false;
    this._outResolve = null;

    this.initialize();
  }

  /**
   * Create background gradient texture (linear + radial).
   */
  createGradientTexture(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // Diagonal gradient: top-left corner → bottom-right corner
    const linear = ctx.createLinearGradient(0, 0, width, height);
    linear.addColorStop(0, "#2a1f5e");
    linear.addColorStop(0.4, "#150e3a");
    linear.addColorStop(1, "#050020");
    ctx.fillStyle = linear;
    ctx.fillRect(0, 0, width, height);

    return PIXI.Texture.from(canvas);
  }

  /**
   * Create or update the gradient background sprite.
   */
  applyGradientBackground() {
    if (!this.dimensions) return;

    const { width, height } = this.dimensions;

    if (this.gradientTexture) {
      this.gradientTexture.destroy(true);
      this.gradientTexture = null;
    }

    this.gradientTexture = this.createGradientTexture(width, height);

    if (!this.gradientSprite) {
      this.gradientSprite = new PIXI.Sprite(this.gradientTexture);
      this.gradientSprite.zIndex = 1;
      this.gradientSprite.position.set(0, 0);
      this.container.addChild(this.gradientSprite);
    } else {
      this.gradientSprite.texture = this.gradientTexture;
    }
  }

  /**
   * Force-load the loading_screen spine and start the "in" animation.
   */
  async initialize() {
    try {
      this.applyGradientBackground();
      const BASE_PATH = getAssetsPath();
      const jsonPath = `${BASE_PATH}/spines/loading_screen/loading_screen.json`;
      const atlasPath = `${BASE_PATH}/spines/loading_screen/loading_screen.atlas`;
      const skeletonAlias = "loading-screen-skeleton";
      const atlasAlias = "loading-screen-atlas";

      // Force-load if not already cached
      if (!PIXI.Assets.cache.has(skeletonAlias)) {
        PIXI.Assets.add({ alias: skeletonAlias, src: jsonPath });
        PIXI.Assets.add({ alias: atlasAlias, src: atlasPath });
        await PIXI.Assets.load([skeletonAlias, atlasAlias]);
      }

      this.spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.spine?.skeleton?.data?.animations) return;

      this.spine.zIndex = 2;
      this.spine.visible = true;
      this.spine.alpha = 1;
      this.spine.skeleton.setToSetupPose();
      this.container.addChild(this.spine);

      this.isInitialized = true;
      this.applyScaleAndCenter();
      this.playIn();
    } catch (error) {
      console.warn("[LoaderController] Failed to initialize:", error);
    }
  }

  /**
   * Scale and center the spine based on device config.
   */
  applyScaleAndCenter() {
    if (!this.dimensions || !this.isInitialized || !this.spine) return;

    const { width, height } = this.dimensions;
    const config = this.getDeviceConfig();

    // Responsive scale: fit spine to 80% of the smaller viewport dimension
    // SPINE_REFERENCE_SIZE is the approximate spine width/height at scale=1
    const SPINE_REFERENCE_SIZE = 1200;
    const fitDimension = Math.min(width, height) * 0.8;
    const responsiveScale = fitDimension / SPINE_REFERENCE_SIZE;

    // Use the smaller of responsive scale and device max scale
    const finalScale = Math.min(responsiveScale, config.scale);
    this.spine.scale.set(finalScale);

    this.spine.x = width / 2;
    this.spine.y = height / 2 + config.yOffset;
  }

  /**
   * Get device-specific scale and offset config.
   */
  getDeviceConfig() {
    if (!this.dimensionsContext) return DEVICE_CONFIG_DESKTOP;

    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? DEVICE_CONFIG_MOBILE_PORTRAIT
        : DEVICE_CONFIG_MOBILE_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? DEVICE_CONFIG_TABLET_PORTRAIT
        : DEVICE_CONFIG_TABLET_LANDSCAPE;
    }
    return DEVICE_CONFIG_DESKTOP;
  }

  /**
   * Play "in" → then queue "loop".
   */
  playIn() {
    if (!this.spine?.state) return;

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();

    // Play "in" once
    this.spine.state.setAnimation(0, "in", false);

    // Queue "loop" to start right after "in" finishes
    this.spine.state.addAnimation(0, "loop", true, 0);

    // Listen for when "in" completes
    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation?.name === "in") {
          this.inComplete = true;
          // If loading already finished while "in" was playing, start "out" now
          if (this.loadingComplete) {
            this.playOut();
          }
        }
      },
    });
  }

  /**
   * Called when assets are fully loaded.
   * If "in" is already done, plays "out" immediately.
   * Otherwise, flags it so "out" plays once "in" completes.
   * Returns a Promise that resolves when "out" animation finishes.
   */
  onLoadingComplete() {
    return new Promise((resolve) => {
      if (!this.isInitialized || !this.spine?.state) {
        resolve();
        return;
      }

      this.loadingComplete = true;
      this._outResolve = resolve;

      if (this.inComplete) {
        this.playOut();
      }
      // else: playIn listener will call playOut when ready
    });
  }

  /**
   * Play "out" animation, then resolve the promise.
   */
  playOut() {
    if (!this.spine?.state) {
      this._outResolve?.();
      return;
    }

    // Clear existing listeners to avoid double-fire
    this.spine.state.clearListeners();

    // Play "out" once (replaces the loop)
    this.spine.state.setAnimation(0, "out", false);

    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation?.name === "out") {
          this._outResolve?.();
          this._outResolve = null;
        }
      },
    });
  }

  updateDimensions(dimensions, dimensionsContext) {
    this.dimensions = dimensions;
    if (dimensionsContext) {
      this.dimensionsContext = dimensionsContext;
    }

    this.applyGradientBackground();

    if (this.isInitialized) {
      this.applyScaleAndCenter();
    }

    // Update error UI if showing
    if (this.errorContainer) {
      this.errorContainer.x = dimensions.width / 2;
      this.errorContainer.y = dimensions.height / 2;
    }
  }

  /**
   * Show error fallback UI — removes spine, displays error message + reload button.
   * Uses only PixiJS primitives (no external assets needed).
   */
  showError(message = "Something went wrong.\nPlease reload the game.") {
    // Remove spine if it exists
    if (this.spine) {
      try {
        if (this.spine.state) {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        }
        if (this.container?.children?.includes(this.spine)) {
          this.container.removeChild(this.spine);
        }
        this.spine.destroy();
      } catch {
        // Silent fail
      }
      this.spine = null;
    }

    // Ensure gradient background is showing
    this.applyGradientBackground();

    const { width, height } = this.dimensions || { width: 800, height: 600 };

    // Create error container centered on screen
    this.errorContainer = new PIXI.Container();
    this.errorContainer.zIndex = 10;
    this.errorContainer.x = width / 2;
    this.errorContainer.y = height / 2;
    this.container.addChild(this.errorContainer);

    // Error message text
    const messageText = new PIXI.Text(message, {
      fontFamily: "Arial, sans-serif",
      fontSize: 28,
      fontWeight: "600",
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: 500,
    });
    messageText.anchor.set(0.5);
    messageText.y = -40;
    this.errorContainer.addChild(messageText);

    // Reload button
    const buttonWidth = 200;
    const buttonHeight = 50;
    const buttonY = messageText.y + messageText.height / 2 + 100;

    // Button background
    const buttonBg = new PIXI.Graphics();
    buttonBg.beginFill(0x4a2d8a);
    buttonBg.drawRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
    );
    buttonBg.endFill();
    // Subtle border
    buttonBg.lineStyle(2, 0x7b5cbf, 0.8);
    buttonBg.drawRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
    );
    buttonBg.y = buttonY;
    buttonBg.eventMode = "static";
    buttonBg.cursor = "pointer";

    // Hover effects
    buttonBg.on("pointerover", () => {
      buttonBg.tint = 0xccbbff;
    });
    buttonBg.on("pointerout", () => {
      buttonBg.tint = 0xffffff;
    });
    buttonBg.on("pointertap", () => {
      window.location.reload();
    });

    this.errorContainer.addChild(buttonBg);

    // Button text
    const buttonText = new PIXI.Text("Reload", {
      fontFamily: "Arial, sans-serif",
      fontSize: 22,
      fontWeight: "700",
      fill: 0xffffff,
    });
    buttonText.anchor.set(0.5);
    buttonText.y = buttonY;
    buttonText.eventMode = "none";
    this.errorContainer.addChild(buttonText);
  }

  cleanup() {
    // Cleanup gradient
    if (this.gradientSprite) {
      try {
        if (this.container?.children?.includes(this.gradientSprite)) {
          this.container.removeChild(this.gradientSprite);
        }
        this.gradientSprite.destroy();
      } catch {
        // Silent fail
      }
      this.gradientSprite = null;
    }
    if (this.gradientTexture) {
      try {
        this.gradientTexture.destroy(true);
      } catch {
        // Silent fail
      }
      this.gradientTexture = null;
    }

    // Cleanup spine
    if (this.spine) {
      try {
        if (this.spine.state) {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        }
        if (this.container?.children?.includes(this.spine)) {
          this.container.removeChild(this.spine);
        }
        this.spine.destroy();
      } catch {
        // Silent fail
      }
      this.spine = null;
    }

    this.isInitialized = false;
    this._outResolve = null;
  }
}

export default LoaderController;
